import type { ProviderDetail } from '../../../../../js/api';
import { ECFMG_SECTIONS } from './credentials';
import { DocumentSections, SectionSpec } from './DocumentSections';
import { LegacyUploadsNote } from './LegacyUploadsNote';
import { fileCountFor, ProviderDrawerMaps, providerFileName } from './providerDisplay';

/**
 * The Education tab's sections, in training order: the education DocumentTypes and,
 * between fellowship and CME, the ECFMG certificate — a credential ENTRY (certificate
 * number + dates over its document). `required` flags the add bar and the tab label:
 * only the diploma and the residency are expected of everyone (internships and
 * fellowships are not part of every training path, the ECFMG only of IMGs).
 */
export const EDUCATION_DOCUMENT_TYPES: readonly SectionSpec[] = [
  { slug: 'medical-diploma', required: true },
  { slug: 'internship', required: false },
  { slug: 'residency-certificate', required: true },
  { slug: 'fellowship-certificate', required: false },
  ...ECFMG_SECTIONS,
  { slug: 'cme', required: false },
];

/**
 * The `ProviderFileType` codes of the LEGACY uploads of the same kinds (files only, on
 * the old profile — counted in the amber note, never listed here).
 */
const LEGACY_EDUCATION_FILE_TYPES = [
  'MEDICAL_DIPLOMA',
  'INTERNSHIP',
  'RESIDENCY_CERTIFICATE',
  'FELLOWSHIP_CERTIFICATE',
  'ECFMG',
  'CME',
] as const;

/**
 * Education documents (languages live on General). Most clinicians going through
 * credentialing need the diploma and residency, so the gaps are one click to fill:
 * the standard sections — cards for what is on file, one add bar for the rest.
 */
export function EducationTab({
  provider,
  maps,
  onOpenDocuments,
}: {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
  /** Switches the drawer to the Documents tab (legacy uploads live there). */
  onOpenDocuments: () => void;
}) {
  const legacyUploads = LEGACY_EDUCATION_FILE_TYPES.map((legacyFileType) => ({
    label: maps.ProviderFileType?.[legacyFileType] || legacyFileType,
    count: fileCountFor(provider.file_counts, legacyFileType),
  })).filter((upload) => upload.count > 0);

  return (
    <div className="tw:flex tw:flex-col tw:gap-4" data-testid="provider-education-documents">
      <DocumentSections
        providerId={provider.id}
        types={EDUCATION_DOCUMENT_TYPES}
        maps={maps}
        testIdPrefix="provider-education"
        zipSubject={`${providerFileName(provider)} Education Documents`}
        emptyText="No education documents on file."
        loadingText="Loading education documents..."
      />
      {legacyUploads.length > 0 && (
        <LegacyUploadsNote
          summary={`Uploads not yet in Documents: ${legacyUploads
            .map((upload) => `${upload.label} (${upload.count})`)
            .join(', ')}.`}
          onOpenDocuments={onOpenDocuments}
          data-testid="provider-education-legacy"
        />
      )}
    </div>
  );
}
